// How long the track has to sit still before a swipe counts as landed. Long
// enough not to fire part-way through a flick, short enough that the next
// rotation isn't left hanging.
const SETTLE_MS = 150;

class FeaturedReviews extends HTMLElement {
	connectedCallback() {
		this.track = this.querySelector('.featured-reviews__slides');
		this.slides = [...this.querySelectorAll('.featured-reviews__slide')];
		this.index = 0;
		this.intervalMs = (parseFloat(this.dataset.interval) || 5) * 1000;

		if (this.slides.length < 2) return;

		// Wired up before the reduced-motion bail: rotating on a timer is the
		// motion someone opted out of, scrolling the track yourself isn't.
		this.initScroll();

		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		this.rotates = true;

		// Only rotate while on screen
		this.observer = new IntersectionObserver(([entry]) => {
			this.visible = entry.isIntersecting;
			if (this.visible) this.start();
			else this.stop();
		}, { threshold: 0.4 });
		this.observer.observe(this);
	}

	/* Keep the indicator and the timer in step with the native scroll.
	 *
	 * Nothing here drives the scrolling: the slides are on a scroll-snap track,
	 * so the browser owns the gesture, the momentum and the snap (WEB-3176).
	 */
	initScroll() {
		this.track.addEventListener('scroll', () => {
			// The automatic transition moves the track itself and its fade
			// already covers the jump — read that as a swipe and the thumb
			// teleports across instead of gliding.
			if (this.transitioning) return;

			if (!this.frame) {
				this.frame = requestAnimationFrame(() => {
					this.frame = null;
					this.classList.add('is-scrolling');
					this.style.setProperty('--index', this.track.scrollLeft / this.track.clientWidth);
				});
			}

			// Hold the next rotation off until the swipe has landed, so a slide
			// can't be whipped away mid-gesture.
			this.stop();
			clearTimeout(this.settle);
			this.settle = setTimeout(() => this.onScrollEnd(), SETTLE_MS);
		}, { passive: true });

		// The track is a tab stop now that it's the only way to reach the other
		// reviews, so don't rotate slides out from under someone reading one.
		// Focus-visible rather than focus: a click that happens to land on the
		// track shouldn't stop the rotation for good.
		this.track.addEventListener('focusin', () => {
			this.focused = this.track.matches(':focus-visible');
			if (this.focused) this.stop();
		});

		this.track.addEventListener('focusout', () => {
			this.focused = false;
			this.start();
		});

		// A resize re-snaps the track without saying where it landed, so put it
		// back on the slide we think is showing.
		this.resizeObserver = new ResizeObserver(() => {
			if (!this.transitioning) this.track.scrollLeft = this.slides[this.index].offsetLeft;
		});
		this.resizeObserver.observe(this.track);
	}

	/* Give the swiped-to slide its full dwell time, instead of picking up a
	   timer that was already part-way through. */
	onScrollEnd() {
		this.index = Math.round(this.track.scrollLeft / this.track.clientWidth);
		this.classList.remove('is-scrolling');
		this.style.setProperty('--index', this.index);
		this.start();
	}

	disconnectedCallback() {
		this.stop();
		clearTimeout(this.settle);
		this.observer?.disconnect();
		this.resizeObserver?.disconnect();
	}

	start() {
		if (this.timer || !this.rotates || !this.visible || this.focused) return;
		this.timer = setInterval(() => this.show((this.index + 1) % this.slides.length), this.intervalMs);
	}

	stop() {
		clearInterval(this.timer);
		this.timer = null;
	}

	/* Move to a slide without the move reading as a scroll.
	 *
	 * The slides can't cross-fade in place now they sit side by side on the
	 * track rather than stacked in one cell, so: jump the track across
	 * instantly, then pin the slide left behind back over the top of the one
	 * that's arrived and cross-fade the two there. Same trick carousel-v2 uses
	 * to fade between autoplay slides.
	 */
	show(index) {
		const outgoing = this.slides[this.index];
		const incoming = this.slides[index];

		this.transitioning = true;
		this.index = index;

		// Mandatory snapping fights a scroll position set from script, and the
		// pinned slide moves the scrollable area while it's out of place.
		this.track.style.scrollSnapType = 'none';
		this.track.scrollLeft = incoming.offsetLeft;

		// Read back rather than reused: this is how far the outgoing slide has
		// to come to sit where the viewport ended up.
		outgoing.style.transform = `translateX(${this.track.scrollLeft - outgoing.offsetLeft}px)`;
		outgoing.classList.add('is-leaving');
		incoming.classList.add('is-entering');

		outgoing.addEventListener('animationend', () => {
			outgoing.classList.remove('is-leaving');
			incoming.classList.remove('is-entering');
			outgoing.style.transform = '';
			this.track.style.scrollSnapType = '';
			this.transitioning = false;
		}, { once: true });

		this.style.setProperty('--index', index);
	}
}

if (!customElements.get('featured-reviews')) customElements.define('featured-reviews', FeaturedReviews);
